import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import api from '../../services/api';
import {
  RefreshCw, ArrowRightLeft, CheckCircle2, XCircle,
  DollarSign, TrendingUp, TrendingDown, Shield,
  Clock, Landmark, Filter, Download,
} from 'lucide-react';

/* ───────────────────────── types ───────────────────────── */

interface AdminPosition {
  id: string;
  tenantId: string;
  userId: string;
  asset: string;
  cryptoAmount: number;
  costBasisUsd: number;
  costBasisRate: number;
  status: string;
  currentPrice: number | null;
  currentValue: number | null;
  pnl: number | null;
  pnlPercent: number | null;
  reserveId: string | null;
  assignmentId: string | null;
  cashout: {
    requestedAt: string;
    rate: number;
    usd: number;
    feeUsd: number;
    achTransferId: string;
    settledAt: string | null;
  } | null;
  user?: {
    email: string;
    firstName: string;
    lastName: string;
  };
  createdAt: string;
}

interface AdminStats {
  byAsset: Record<string, Record<string, { count: number; totalInvested: number; totalCrypto: number; currentValue: number }>>;
  totals: {
    totalInvested: number;
    totalCurrentValue: number;
    totalPnl: number;
    positionCount: number;
  };
  prices: Record<string, number>;
}

interface ReserveSummary {
  tenant_id: string;
  asset: string;
  locked_count: number;
  total_locked_shares: number;
  total_locked_usd: number;
  total_locked_crypto: number;
  settled_count: number;
  total_settled_usd: number;
}

/* ───────────────────────── helpers ───────────────────────── */

const formatUsd = (n: number | null) => {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
};

const formatCrypto = (n: number, asset: string) => {
  if (!n) return '0';
  const decimals = asset === 'BTC' ? 8 : asset === 'ETH' ? 6 : 4;
  return n.toFixed(decimals);
};

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('en-US', {
  month: 'short', day: 'numeric', year: 'numeric',
});

const STATUS_STYLES: Record<string, string> = {
  active:      'bg-emerald-500/10 text-emerald-400',
  cashing_out: 'bg-yellow-500/10 text-yellow-400',
  settled:     'bg-blue-500/10 text-blue-400',
  cancelled:   'bg-muted text-muted-foreground',
};

/* ═══════════════════════════════════════════════════════════════════
   SHARE CONVERSION ADMIN TAB
   ═══════════════════════════════════════════════════════════════════ */

const ShareConversionAdminTab: React.FC = () => {
  const [positions, setPositions] = useState<AdminPosition[]>([]);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [reserves, setReserves] = useState<ReserveSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filters
  const [filterStatus, setFilterStatus] = useState('');
  const [filterAsset, setFilterAsset] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qp = new URLSearchParams();
      if (filterStatus) qp.set('status', filterStatus);
      if (filterAsset)  qp.set('asset', filterAsset);
      qp.set('limit', '100');

      const [posRes, statsRes, resRes] = await Promise.all([
        api.get(`/share-conversion/admin/positions?${qp.toString()}`).catch(() => null) as Promise<any>,
        api.get('/share-conversion/admin/stats').catch(() => null) as Promise<any>,
        api.get('/admin/treasury/contracts/reserves/summary').catch(() => null) as Promise<any>,
      ]);

      if (posRes?.data)   setPositions(Array.isArray(posRes.data) ? posRes.data : []);
      if (statsRes?.data)  setStats(statsRes.data);
      if (resRes?.data)    setReserves(Array.isArray(resRes.data) ? resRes.data : []);
    } catch (err) {
      console.error('ShareConversion admin load error', err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterAsset]);

  useEffect(() => { load(); }, [load]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
    toast.success('Refreshed');
  };

  const handleSettle = async (positionId: string) => {
    if (!window.confirm('Mark this position as settled? ACH must have completed.')) return;
    try {
      await api.post(`/share-conversion/admin/settle/${positionId}`);
      toast.success('Position settled');
      await load();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to settle');
    }
  };

  const handleCancel = async (positionId: string) => {
    const notes = window.prompt('Cancellation reason (optional):');
    if (notes === null) return; // user hit cancel
    try {
      await api.post(`/share-conversion/admin/cancel/${positionId}`, { notes });
      toast.success('Position cancelled');
      await load();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to cancel');
    }
  };

  if (loading) return <div className="py-12 flex justify-center"><LoadingSpinner /></div>;

  return (
    <div className="space-y-6">
      {/* Stats overview */}
      {stats && (
        <div className="space-y-4">
          {/* Top-level cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatsCard label="Active Positions" value={stats.totals.positionCount}
              icon={<ArrowRightLeft className="w-5 h-5 text-primary" />} />
            <StatsCard label="Total Invested" value={formatUsd(stats.totals.totalInvested)}
              icon={<DollarSign className="w-5 h-5 text-primary" />} />
            <StatsCard label="Current Value" value={formatUsd(stats.totals.totalCurrentValue)}
              icon={<TrendingUp className="w-5 h-5 text-primary" />} />
            <StatsCard label="Unrealized P&L" value={formatUsd(stats.totals.totalPnl)}
              icon={stats.totals.totalPnl >= 0
                ? <TrendingUp className="w-5 h-5 text-emerald-500" />
                : <TrendingDown className="w-5 h-5 text-red-500" />}
              valueClass={stats.totals.totalPnl >= 0 ? 'text-emerald-400' : 'text-red-400'} />
          </div>

          {/* Per-asset breakdown */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {Object.entries(stats.prices).map(([asset, price]) => {
              const assetData = stats.byAsset[asset];
              const active = assetData?.active || { count: 0, totalInvested: 0, totalCrypto: 0, currentValue: 0 };
              return (
                <div key={asset} className="bg-card border border-border rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-lg">{asset}</span>
                    <span className="text-sm text-muted-foreground">${Number(price).toLocaleString()}</span>
                  </div>
                  <div className="space-y-1 text-sm text-muted-foreground">
                    <p>{active.count} active · ${Number(active.totalInvested).toLocaleString()} invested</p>
                    <p>Value: ${Number(active.currentValue).toLocaleString()}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Reserve / Borrowing Power summary */}
      {reserves.length > 0 && (
        <div className="bg-card border border-primary/20 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-5 h-5 text-primary" />
            <h3 className="text-sm font-semibold">Tokenized Reserve &amp; Borrowing Power</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            Shares locked 1:1 against crypto positions. Tenants can use the tokenized reserve as borrowing collateral
            from banking or lending partners without needing to sell shares.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {reserves.map((r) => (
              <div key={r.asset} className="border border-border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="font-bold">{r.asset}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${r.locked_count > 0 ? 'bg-yellow-500/10 text-yellow-400' : 'bg-muted text-muted-foreground'}`}>
                    {r.locked_count} locked
                  </span>
                </div>
                <div className="mt-2 space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Locked Shares</span>
                    <span className="font-medium">{Number(r.total_locked_shares).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Reserve Value (USD)</span>
                    <span className="font-medium">{formatUsd(Number(r.total_locked_usd) * 100)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Crypto Held</span>
                    <span className="font-medium">{formatCrypto(Number(r.total_locked_crypto), r.asset)} {r.asset}</span>
                  </div>
                  <div className="flex justify-between border-t border-border pt-1 mt-1">
                    <span className="text-muted-foreground">Settled</span>
                    <span>{r.settled_count} · {formatUsd(Number(r.total_settled_usd) * 100)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters + actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="text-lg font-semibold">All Positions</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={filterAsset} onChange={(e) => setFilterAsset(e.target.value)}
            className="px-2 py-1.5 text-sm border border-border rounded-lg bg-background">
            <option value="">All Assets</option>
            <option value="BTC">BTC</option>
            <option value="ETH">ETH</option>
            <option value="XRP">XRP</option>
            <option value="USDC">USDC</option>
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
            className="px-2 py-1.5 text-sm border border-border rounded-lg bg-background">
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="cashing_out">Cashing Out</option>
            <option value="settled">Settled</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <button onClick={handleRefresh} disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-muted transition">
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Positions table */}
      {positions.length === 0 ? (
        <div className="bg-card border border-dashed border-border rounded-xl p-12 text-center">
          <ArrowRightLeft className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <h3 className="font-semibold mb-1">No positions found</h3>
          <p className="text-sm text-muted-foreground">Share conversion positions will appear once investors convert USD into crypto.</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Investor</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Asset</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">Amount</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">Cost Basis</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">Current Value</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">P&L</th>
                  <th className="text-center px-4 py-3 font-medium text-muted-foreground">Reserve</th>
                  <th className="text-center px-4 py-3 font-medium text-muted-foreground">Status</th>
                  <th className="text-center px-4 py-3 font-medium text-muted-foreground">Date</th>
                  <th className="text-center px-4 py-3 font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {positions.map((p) => (
                  <tr key={p.id} className="border-b border-border last:border-0 hover:bg-muted/20 transition">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium truncate max-w-[180px]">
                          {p.user ? `${p.user.firstName || ''} ${p.user.lastName || ''}`.trim() || p.user.email : '—'}
                        </p>
                        <p className="text-xs text-muted-foreground truncate max-w-[180px]">{p.user?.email || ''}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono font-bold">{p.asset}</td>
                    <td className="px-4 py-3 text-right font-mono">{formatCrypto(p.cryptoAmount, p.asset)}</td>
                    <td className="px-4 py-3 text-right">{formatUsd(p.costBasisUsd)}</td>
                    <td className="px-4 py-3 text-right">{formatUsd(p.currentValue)}</td>
                    <td className={`px-4 py-3 text-right font-medium ${p.pnl !== null ? (p.pnl >= 0 ? 'text-emerald-400' : 'text-red-400') : ''}`}>
                      {p.pnl !== null ? `${p.pnl >= 0 ? '+' : ''}${formatUsd(p.pnl)}` : '—'}
                      {p.pnlPercent !== null && (
                        <span className="block text-xs text-muted-foreground">{p.pnlPercent >= 0 ? '+' : ''}{p.pnlPercent}%</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {p.reserveId ? (
                        <span title="Reserve-backed"><Shield className="w-4 h-4 text-yellow-400 mx-auto" /></span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_STYLES[p.status] || ''}`}>
                        {p.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-xs text-muted-foreground whitespace-nowrap">
                      {fmtDate(p.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {p.status === 'cashing_out' && (
                          <button onClick={() => handleSettle(p.id)} title="Mark settled"
                            className="p-1 rounded hover:bg-emerald-500/10 text-emerald-400">
                            <CheckCircle2 className="w-4 h-4" />
                          </button>
                        )}
                        {(p.status === 'active' || p.status === 'cashing_out') && (
                          <button onClick={() => handleCancel(p.id)} title="Cancel position"
                            className="p-1 rounded hover:bg-red-500/10 text-red-400">
                            <XCircle className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};


/* ═══════════════════════════════════════════════════════════════════
   STAT CARD HELPER
   ═══════════════════════════════════════════════════════════════════ */

const StatsCard: React.FC<{
  label: string;
  value: string | number;
  icon: React.ReactNode;
  valueClass?: string;
}> = ({ label, value, icon, valueClass }) => (
  <div className="bg-card border border-border rounded-xl p-4">
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <span className="text-sm font-medium text-muted-foreground">{label}</span>
      {icon}
    </div>
    <p className={`text-2xl font-bold mt-1 ${valueClass || ''}`}>{value}</p>
  </div>
);

export { ShareConversionAdminTab };
export default ShareConversionAdminTab;
